import { useNavigate } from 'react-router';
import {
    RequestStatus,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import {
    mandatoryFieldErrorMessage,
    maxFieldErrorMessage,
    minFieldErrorMessage,
    tooManyCharsMessage,
} from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { ParkingPortalLayoutWithHeader } from '../mobile/layout/ParkingPortalLayoutWithHeader.tsx';
import { Box, Button } from '@mui/material';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { SubmitButton } from '../ui/buttons/SubmitButton.tsx';
import { useOffstreetGuestDayPassOutlet } from './OperatorGuestDayPassOverview.tsx';
import Alert from '@mui/material/Alert';
import { OperatorFormSelect } from '../ui/form/OperatorFormSelect.tsx';
import { GuestPassDurationCalculationMode } from 'product-shared/product-template/ProductTemplateEnums.ts';
import { useEffect } from 'react';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';

const enum OffstreetGuestDayPassAddStackFields {
    contractTemplateId = 'contractTemplateId',
    stackName = 'stackName',
    numberOfDays = 'numberOfDays',
    numberGuestDayPassContracts = 'numberGuestDayPassContracts',
    validTo = 'validTo',
}

interface OffstreetGuestDayPassAddFieldValues {
    [OffstreetGuestDayPassAddStackFields.contractTemplateId]: number | null;
    [OffstreetGuestDayPassAddStackFields.stackName]: string;
    [OffstreetGuestDayPassAddStackFields.numberOfDays]: number | null;
    [OffstreetGuestDayPassAddStackFields.numberGuestDayPassContracts]:
        | number
        | null;
    [OffstreetGuestDayPassAddStackFields.validTo]: DateTime | null;
}

interface CreateGuestDayPassAddStackPayload {
    contractTemplateId: number;
    stackName: string;
    numberOfDays: number;
    numberGuestDayPassContracts: number | null;
    validTo: DateTime;
}

export const MAX_GUEST_DAY_PASS_STACK_CONTRACTS = 1000;

export function OperatorGuestDayPassAdd() {
    const { refetchDayPassInfo, guestDayPassConfigInfos } =
        useOffstreetGuestDayPassOutlet();
    const navigate = useNavigate();
    const [
        createOffstreetGuestDayPassStackState,
        createOffstreetGuestDayPassStack,
    ] = useServerWrite<CreateGuestDayPassAddStackPayload, null>(() => ({
        url: `/ui-api/operator-account/offstreet-guest-day-pass/stack/add`,
    }));
    useServerSuccessEffect(createOffstreetGuestDayPassStackState, () => {
        refetchDayPassInfo();
        navigate('..');
    });

    const { control, handleSubmit, setValue } =
        useForm<OffstreetGuestDayPassAddFieldValues>({
            defaultValues: {
                [OffstreetGuestDayPassAddStackFields.contractTemplateId]:
                    guestDayPassConfigInfos.length === 1
                        ? guestDayPassConfigInfos[0].contractTemplateId
                        : null,
                [OffstreetGuestDayPassAddStackFields.stackName]: '',
                [OffstreetGuestDayPassAddStackFields.numberOfDays]: null,
                [OffstreetGuestDayPassAddStackFields.numberGuestDayPassContracts]:
                    null,
                [OffstreetGuestDayPassAddStackFields.validTo]: null,
            },
            mode: 'onBlur',
        });

    const maxLengthDescription = 30;
    const descriptionTooLong = useTranslation(
        tooManyCharsMessage(maxLengthDescription),
    );
    const minNumberOfEventContracts = 1;
    const minimumMessage = useTranslation(
        minFieldErrorMessage(minNumberOfEventContracts),
    );
    const maximumMessage = useTranslation(
        maxFieldErrorMessage(MAX_GUEST_DAY_PASS_STACK_CONTRACTS),
    );
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    const savePending =
        createOffstreetGuestDayPassStackState.status == RequestStatus.PENDING;
    const contractTemplateId = useWatch({
        control,
        name: OffstreetGuestDayPassAddStackFields.contractTemplateId,
    });
    const selectedConfig = guestDayPassConfigInfos.find(
        t => t.contractTemplateId === contractTemplateId,
    )?.guestDayPassConfig;
    const is24HourDayConfig =
        selectedConfig?.durationCalculationMode ===
        GuestPassDurationCalculationMode.TWENTY_FOUR_HOUR_DAY;

    useEffect(() => {
        if (!selectedConfig) return;

        setValue(
            OffstreetGuestDayPassAddStackFields.numberOfDays,
            selectedConfig.durationDayOptions.length === 1
                ? selectedConfig.durationDayOptions[0]
                : null,
        );
    }, [selectedConfig, setValue, guestDayPassConfigInfos]);

    return (
        <OperatorRoutedModal
            variant={ModalVariant.TINY}
            backUrl={'..'}
            style={{ maxHeight: undefined }}
            render={() => (
                <ParkingPortalLayoutWithHeader
                    title={
                        <Localized
                            de="Stapel generieren"
                            fr="Générer set"
                            it="Genera set"
                            en="Generate stack"
                        />
                    }
                    backTo={'..'}
                >
                    <Box
                        component="form"
                        onSubmit={handleSubmit(
                            (data: OffstreetGuestDayPassAddFieldValues) => {
                                const contractTemplateId =
                                    data[
                                        OffstreetGuestDayPassAddStackFields
                                            .contractTemplateId
                                    ];
                                if (!contractTemplateId) {
                                    throw new Error(
                                        'contractTemplateId in guest day pass must be defined',
                                    );
                                }
                                const numberOfDays =
                                    data[
                                        OffstreetGuestDayPassAddStackFields
                                            .numberOfDays
                                    ];
                                if (!numberOfDays) {
                                    throw new Error(
                                        'numberOfDays in guest day pass must be defined',
                                    );
                                }
                                const validTo =
                                    data[
                                        OffstreetGuestDayPassAddStackFields
                                            .validTo
                                    ];
                                if (!validTo) {
                                    throw new Error(
                                        'validTo in guest day pass must be defined',
                                    );
                                }
                                createOffstreetGuestDayPassStack({
                                    ...data,
                                    contractTemplateId,
                                    numberOfDays,
                                    validTo,
                                });
                            },
                        )}
                        sx={{
                            padding: '25px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        {createOffstreetGuestDayPassStackState.status ===
                            RequestStatus.ERROR && (
                            <Alert severity="error" sx={{ margin: '0' }}>
                                <Localized
                                    de="Es ist ein Fehler aufgetreten."
                                    fr="Une erreur est survenue."
                                    it="Si è verificato un errore."
                                    en="An error has occurred."
                                />
                            </Alert>
                        )}
                        {guestDayPassConfigInfos.length > 1 && (
                            <OperatorFormSelect
                                label={
                                    <Localized
                                        de="Produkt-Typ"
                                        fr="Type de produit"
                                        it="Tipo prodotto"
                                        en="Product type"
                                    />
                                }
                                name={
                                    OffstreetGuestDayPassAddStackFields.contractTemplateId
                                }
                                control={control}
                                options={guestDayPassConfigInfos.map(
                                    configInfo => ({
                                        display: (
                                            <Localized
                                                {...configInfo
                                                    .guestDayPassConfig
                                                    .productName}
                                            />
                                        ),
                                        value: configInfo.contractTemplateId,
                                    }),
                                )}
                                rules={{
                                    required: {
                                        value: true,
                                        message: mandatoryFieldMessage,
                                    },
                                }}
                            />
                        )}
                        <ReactHookFormTextField
                            label={
                                <Localized
                                    de="Bezeichnung"
                                    fr="Désignation"
                                    it="Descrizione"
                                    en="Description"
                                />
                            }
                            name={OffstreetGuestDayPassAddStackFields.stackName}
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: mandatoryFieldMessage,
                                },
                                maxLength: {
                                    value: maxLengthDescription,
                                    message: descriptionTooLong,
                                },
                                minLength: {
                                    value: 1,
                                    message: mandatoryFieldMessage,
                                },
                            }}
                        />
                        <OperatorFormSelect
                            readonly={
                                !selectedConfig ||
                                selectedConfig.durationDayOptions.length === 1
                            }
                            label={
                                is24HourDayConfig ? (
                                    <Localized
                                        de="Anzahl Tage (24h) pro Pass"
                                        fr="Nombre de jours (24h) par passe"
                                        it="Numero giorni (24h) per pass"
                                        en="Days (24h) per pass"
                                    />
                                ) : (
                                    <Localized
                                        de="Anzahl Kalendertage pro Pass"
                                        fr="Nombre de jours calendaires par passe"
                                        it="Numero giorni di calendario per pass"
                                        en="Calendar days per pass"
                                    />
                                )
                            }
                            name={
                                OffstreetGuestDayPassAddStackFields.numberOfDays
                            }
                            control={control}
                            options={
                                selectedConfig?.durationDayOptions.map(
                                    days => ({
                                        display: days,
                                        value: days,
                                    }),
                                ) ?? []
                            }
                            rules={{
                                required: {
                                    value: true,
                                    message: mandatoryFieldMessage,
                                },
                            }}
                        />
                        <ReactHookFormTextField
                            label={
                                <Localized
                                    de="Anzahl Pässe"
                                    fr="Quantité de passes"
                                    it="Quantità di pass"
                                    en="Passes quantity"
                                />
                            }
                            type="number"
                            name={
                                OffstreetGuestDayPassAddStackFields.numberGuestDayPassContracts
                            }
                            control={control}
                            rules={{
                                min: {
                                    value: minNumberOfEventContracts,
                                    message: minimumMessage,
                                },
                                max: {
                                    value: MAX_GUEST_DAY_PASS_STACK_CONTRACTS,
                                    message: maximumMessage,
                                },
                                required: {
                                    value: true,
                                    message: mandatoryFieldMessage,
                                },
                            }}
                            sx={{
                                width: '150px',
                            }}
                        />
                        <Controller
                            name={OffstreetGuestDayPassAddStackFields.validTo}
                            control={control}
                            render={({ field, fieldState }) => (
                                <DesktopDatePicker
                                    format="dd.MM.yyyy"
                                    label={
                                        <Localized
                                            de="Gültig bis"
                                            fr="Valable jusqu'au"
                                            it="Valida fino a"
                                            en="Valid until"
                                        />
                                    }
                                    value={field.value ?? undefined}
                                    onChange={field.onChange}
                                    minDate={DateTime.now()}
                                    maxDate={DateTime.now().plus({
                                        years: 3,
                                    })}
                                    slotProps={{
                                        textField: {
                                            error: !!fieldState.error,
                                            helperText:
                                                fieldState.error?.message,
                                            fullWidth: true,
                                        },
                                    }}
                                    sx={{
                                        paddingBottom: '25px',
                                        width: '150px',
                                    }}
                                />
                            )}
                            rules={{
                                required: {
                                    value: true,
                                    message: mandatoryFieldMessage,
                                },
                            }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                paddingTop: '8px',
                            }}
                        >
                            <Button
                                onClick={() => navigate('..')}
                                variant="outlined"
                                disabled={savePending}
                            >
                                <Localized
                                    de="Abbrechen"
                                    fr="Annuler"
                                    it="Annulla"
                                    en="Cancel"
                                />
                            </Button>
                            <SubmitButton
                                sx={{ marginLeft: '5px' }}
                                loading={savePending}
                                label={
                                    <Localized
                                        de="Speichern"
                                        fr="Sauvegarder"
                                        it="Salva"
                                        en="Save"
                                    />
                                }
                            />
                        </Box>
                    </Box>
                </ParkingPortalLayoutWithHeader>
            )}
        />
    );
}
